/** The one frontend reading of "estimate" - gap G-P7-03, ADR-0222 (Accepted, KD 2026-11-03).
 *
 * The server owns the definition (`backend/app/modules/inventory/estimate.py`: `is_estimate`, and
 * `PRODUCT_READING` / `SERIES_READING`). The screens used to hold three of their own: the legend
 * counted grade C or D (38.0 t), the Estimates tile and the Basis chip counted every figure below
 * grade A (1,278.5 t). KD chose below grade A for all of them (D-28, 2026-11-03). They meet here:
 *
 * - **The tile** is `estimatesAmong(figures, gradedA)`: in the product reading an estimate is every
 *   figure that is not grade A. A backend test asserts the identity against `is_estimate` over the
 *   listed figures, so the two cannot drift.
 * - **The chip** is the server's word (`FigureOut.basis`), drawn by `BasisChip`; it decides nothing.
 * - **The monthly legend** counts what the server says it counts: the series carries
 *   `estimate_reading` (now `below_grade_a`, the tile's) and the sentence is written from it
 *   (`restsOnEstimates`), so a reading that changes again changes the words with it.
 *
 * Nothing else under `src/` may compare a grade to "A" or list C and D to decide an estimate; a
 * test in `estimate.test.ts` scans for exactly that.
 */

import type { components } from "../api/schema";

export type EstimateReading = components["schemas"]["InventoryMonthlyOut"]["estimate_reading"];

/** What a surface counting by each reading says it counts. */
export const READING_WORDS: Readonly<Record<EstimateReading, string>> = {
  below_grade_a: "below grade A",
  grade_c_or_d: "grade C or D",
};

/** The product reading: every grade but A is an estimate. The server's grade, compared as sent. */
export function isBelowGradeA(quality: string): boolean {
  return quality !== "A";
}

/** The Estimates tile's count from a card's own two counts, never from a list of figures. */
export function estimatesAmong(figures: number, gradedA: number): number {
  return figures - gradedA;
}

/** What the Estimates tile and the Basis chip count by (the server's `PRODUCT_READING`). */
export const PRODUCT_READING: EstimateReading = "below_grade_a";

/** The data-quality cell of a record: the grade, and, below grade A, that it is counted as an
 * estimate and by which reading - so a chip saying Estimate beside "Grade B" is explained, not
 * contradicted. */
export function gradeWords(quality: string): string {
  return isBelowGradeA(quality)
    ? `Grade ${quality}, counted as an estimate (${READING_WORDS[PRODUCT_READING]})`
    : `Grade ${quality}`;
}

/** The method cell of a record. A person typing "read or measured" is stored as method metered at
 * grade B; printed bare, "metered" reads as a meter reading EmitIQ holds, beside a chip that says
 * Estimate (G-Wcontrib-15). Said as what it is: a stated reading. */
export function methodWords(method: string, quality: string, origin: string | undefined): string {
  return method === "metered" && origin === "manual" && isBelowGradeA(quality)
    ? "metered, as stated by the person who entered it"
    : method.replace(/_/g, " ");
}

/** "estimates (grade C or D)": the noun a legend uses, with the reading it counts in. */
export function restsOnEstimates(reading: EstimateReading): string {
  return `estimates (${READING_WORDS[reading]})`;
}
